'use client';

import { useDebounceValue } from '@/hooks/use-debounce';
import { cn, formatIDR } from '@/lib/utils';
import { ArrowRightIcon, LoaderIcon, SearchIcon, XIcon } from 'lucide-react';
import { usePathname, useRouter } from 'next/navigation';
import { useEffect, useState, useRef, useCallback } from 'react';
import Cookies from 'js-cookie';
import { usePaginationSearchParams } from '@/hooks/use-pagination';
import { useSearchProduct } from '@/hooks/api/storefront/product';
import { ThemeType } from '@/stores/builder';
import DynamicImage from '@/components/commons/dynamic-image';
import { computePriceView } from '@/lib/product';

export interface ModalSearchProps extends React.HtmlHTMLAttributes<HTMLElement> {
  openModal?: boolean;
  setOpenModal?: (open: boolean) => void;
  host: string;
  locationId?: string;
  preview: boolean;
  theme?: ThemeType;
}

export default function ModalSearch({
  openModal,
  setOpenModal,
  locationId,
  theme,
}: ModalSearchProps) {
  const router = useRouter();
  const pathname = usePathname();
  const modalRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const [keyword, setKeyword] = useState('');
  const [queryParams, setQueryParams] = usePaginationSearchParams();

  const debounceKeyword = useDebounceValue(keyword, 300);
  const showDropdown = keyword.length >= 3;
  const priceType = theme?.productCard?.priceInformation;

  const customerToken = Cookies.get('accessToken');
  const findParams = {
    page: queryParams.pageIndex + 1,
    pageSize: 5,
    q: debounceKeyword,
  };

  const { data: dataSearch, isLoading } = useSearchProduct(
    findParams,
    customerToken,
    locationId as string,
    {
      enabled: debounceKeyword.length >= 3,
    }
  );

  const closeModal = useCallback(() => setOpenModal?.(false), [setOpenModal]);

  const handleSearch = useCallback(
    (kw: string) => {
      if (pathname !== '/search') {
        router.push(`/search?keyword=${encodeURIComponent(kw)}`);
      } else {
        setQueryParams({ keyword: kw, pageIndex: 0 });
      }
      closeModal();
      setKeyword('');
    },
    [pathname, router, setQueryParams, closeModal]
  );

  const handleToProductDetail = useCallback(
    (slug?: string | null) => {
      if (!slug) return;
      router.push(`/products/${encodeURIComponent(slug)}`);
      closeModal();
      setKeyword('');
    },
    [router, closeModal]
  );

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') handleSearch(keyword);
  };

  useEffect(() => {
    if (openModal) {
      document.body.style.overflow = 'hidden';
      inputRef.current?.focus();
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => void (document.body.style.overflow = 'auto');
  }, [openModal]);

  useEffect(() => {
    if (!openModal) return;
    const handler = (e: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
        setKeyword('');
        closeModal();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [openModal, closeModal]);

  return (
    <>
      <div
        className={cn(
          'fixed inset-0 z-[105] flex items-start justify-center bg-black/15',
          openModal ? 'block' : 'hidden'
        )}
      >
        <div
          ref={modalRef}
          className='theme_font padding-header w-full bg-white px-2 py-4 transition-all duration-300'
          onClick={(e) => e.stopPropagation()}
        >
          <div className='theme_layout_page_width mx-auto w-full'>
            <div className='mx-auto flex w-full items-center md:w-1/2'>
              <div className='w-full'>
                <div className='relative'>
                  <input
                    name='keyword'
                    type='text'
                    ref={inputRef}
                    value={keyword}
                    placeholder='Search...'
                    className={cn(
                      'theme_input h-10 w-full px-3 py-1 pr-10',
                      showDropdown && 'rounded-bl-none rounded-br-none'
                    )}
                    onChange={(e) => setKeyword(e.target.value)}
                    onKeyDown={onKeyDown}
                  />
                  <button
                    type='button'
                    onClick={() => handleSearch(keyword)}
                    className='absolute right-2 top-1/2 -translate-y-1/2'
                  >
                    <SearchIcon className='mx-auto w-4' />
                  </button>
                </div>
                {showDropdown && (
                  <div className='theme_font absolute left-0 z-[106] mx-auto mt-0.5 w-full rounded-sm rounded-tl-none rounded-tr-none bg-white p-0 shadow-lg md:w-1/2 md:translate-x-1/2'>
                    <button
                      type='button'
                      className='flex w-full max-w-full justify-between overflow-hidden px-4 py-4'
                      onClick={() => handleSearch(keyword)}
                    >
                      <div className='truncate break-all md:break-words'>
                        Search for &quot;{keyword}&quot;
                      </div>
                      <ArrowRightIcon className='h-4 w-4' />
                    </button>
                    <div className='border-b py-4 '>
                      {isLoading ? (
                        <LoaderIcon className='mx-auto mb-4 h-6 w-6 animate-spin' />
                      ) : (
                        <div className='gap-x-6 px-4 pb-6'>
                          <div className=''>
                            <div className='space-y-2 border-b pb-2 uppercase'>Products</div>
                            {(dataSearch?.data?.length ?? 0) > 0 ? (
                              dataSearch?.data.map((product) => {
                                const lowestPrice =
                                  (Number(product.discount_price) > 0 &&
                                  Number(product.discount_price) < Number(product.sell_price)
                                    ? product.discount_price
                                    : product.sell_price) ?? 0;

                                const { topText, bottomText } = computePriceView(product, {
                                  mode: 'range',
                                });

                                return (
                                  <div
                                    className='flex gap-x-2 p-2 hover:cursor-pointer hover:bg-gray-100'
                                    key={product.id}
                                    onClick={() => handleToProductDetail(product.slug ?? '')}
                                  >
                                    <div className='w-16'>
                                      <DynamicImage
                                        src={
                                          Array.isArray(product.product_assets) &&
                                          typeof product.product_assets[0] === 'string'
                                            ? product.product_assets[0]
                                            : '/path-to-placeholder-image.png'
                                        }
                                        width={100}
                                        height={100}
                                        alt={product.product_name}
                                        className='aspect-square w-16 rounded-sm object-cover'
                                      />
                                    </div>
                                    <div className='w-full max-w-full flex-col overflow-hidden'>
                                      <div className='line-clamp-1 break-all text-sm font-bold md:break-words'>
                                        {product.product_name}
                                      </div>

                                      <div className='theme_search_behavior_product_price mt-1 text-sm'>
                                        {priceType !== 'range' ? (
                                          <>
                                            <div className='mt-1 '>{formatIDR(lowestPrice)}</div>
                                            {Number(product.discount_price) <
                                              Number(product.sell_price) &&
                                              product.sell_price !== lowestPrice && (
                                                <span className='text-xs text-slate-500 line-through'>
                                                  {formatIDR(product.sell_price ?? 0)}
                                                </span>
                                              )}
                                          </>
                                        ) : (
                                          <div className='flex flex-col'>
                                            <div className='mt-1'>{topText}</div>
                                            {bottomText && (
                                              <span className='text-xs text-slate-500 line-through'>
                                                {bottomText}
                                              </span>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })
                            ) : (
                              <div className='p-2 text-sm text-slate-500'>No products found.</div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              <button
                type='button'
                className='flex h-11 items-center p-2'
                onClick={() => setOpenModal?.(false)}
              >
                <XIcon className='mt-2 h-5 w-5' />
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
